'use client';

import React, { useState, useEffect } from 'react';
import { Calculator, ArrowUp } from 'lucide-react';

interface FloatingCalculatorButtonProps {
  calculatorId?: string;
}

export function FloatingCalculatorButton({
  calculatorId = 'mortgage-calculator-container'
}: FloatingCalculatorButtonProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Get calculator element
      const calculatorEl = document.getElementById(calculatorId);
      if (!calculatorEl) return;

      // Get calculator's bottom position
      const calculatorRect = calculatorEl.getBoundingClientRect();
      const calculatorBottom = calculatorRect.bottom;

      // Show button when calculator is scrolled past (bottom is above viewport top)
      // Add 100px buffer so it appears slightly after calculator is out of view
      setIsVisible(calculatorBottom < -100);
    };

    // Check on mount
    handleScroll();

    // Listen to scroll events
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [calculatorId]);

  const scrollToCalculator = () => {
    const calculatorEl = document.getElementById(calculatorId);
    if (!calculatorEl) return;

    // Account for fixed navbar (64px) + padding
    const navbarHeight = 64;
    const padding = 20;
    const targetPosition = calculatorEl.getBoundingClientRect().top + window.scrollY - navbarHeight - padding;

    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  };

  return (
    <button
      onClick={scrollToCalculator}
      className={`fixed bottom-24 sm:bottom-8 right-8 z-50 flex items-center gap-2 px-4 py-3 bg-[#294bfd] text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group ${
        isVisible
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      aria-label="Back to Calculator"
    >
      {/* Icon - visible on all sizes */}
      <Calculator className="w-5 h-5" />

      {/* Text - hidden on mobile, visible on desktop */}
      <span className="hidden sm:inline font-medium">Back to Calculator</span>

      {/* Arrow icon */}
      <ArrowUp className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
    </button>
  );
}
